import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAnimatedNavigate, withViewTransition } from '../hooks/useAnimatedNavigate';
import { ConversationSidebar } from '../components/ConversationSidebar';
import { AgentsListInline } from '../components/agents/AgentsListInline';
import { AgentDetailInline } from '../components/agents/AgentDetailInline';
import { MessageList } from '../components/MessageList';
import { MessageInput } from '../components/MessageInput';
import { HomeScreen } from '../components/home';
import { GraphPanel, type GraphPanelHandle } from '../components/GraphPanel';
import { v2GraphToElements, type V2GraphResponse } from '../components/graph/v2Elements';
import { GraphEntitiesContext } from '../contexts/GraphEntitiesContext';
import { DarkModeToggle } from '../components/DarkModeToggle';
import { LayoutOrientationToggle } from '../components/LayoutOrientationToggle';
import { UserAvatarDropdown } from '../components/UserAvatarDropdown';
import { SearchModal } from '../components/SearchModal';
import { ParticipantPanel } from '../components/ParticipantPanel';
import { useToast } from '../components/Toast';
import { absorbAlerts, injectGeolocationNodes, normalizeEdgeDirections, deduplicateGraphElements, graphEventToElements } from '@cade/graph-logic';
import { trpc } from '../api/trpc';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useAuth } from '../hooks/useAuth';
import { useMobileNav } from '../hooks/useMobileNav';
import { MobileNavToggle, SidebarBackdrop } from '../components/MobileNav';
import { wsManager } from '../api/websocket';
import type { ElementDefinition } from 'cytoscape';
import type { StreamEvent, WebSocketEvent, GraphNode, GraphEdge } from '@cade/shared-types';

export function ChatPage() {
  const navigate = useNavigate();
  const animatedNavigate = useAnimatedNavigate();
  const location = useLocation();
  const { userId } = useAuth();
  const { showToast } = useToast();
  const [activeConversationId, setActiveConversationId] = useState<string | null>(() => {
    // Support both /chat/{id} path and ?c={id} query param
    const pathMatch = location.pathname.match(/^\/chat\/([a-f0-9-]+)$/i);
    if (pathMatch) return pathMatch[1] ?? null;
    const params = new URLSearchParams(location.search);
    return params.get('c');
  });
  const [activeView, setActiveView] = useState<{ type: 'chat' } | { type: 'agents' } | { type: 'agent-detail'; agentId: string }>(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view');
    if (view === 'agents') return { type: 'agents' };
    if (view === 'agent-detail') {
      const agentId = params.get('agentId');
      if (agentId) return { type: 'agent-detail', agentId };
    }
    return { type: 'chat' };
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Mobile-only chrome: the sidebar drawer (closed by default, via useMobileNav)
  // and the graph overlay (collapsed by default). Both are inert on desktop.
  const { isMobile, mobileNavOpen, openNav, closeNav } = useMobileNav();
  const [mobileGraphOpen, setMobileGraphOpen] = useState(false);
  // The mobile graph overlay is collapsed-by-default; re-collapse it whenever the
  // active conversation changes so a new chat never opens straight into the graph.
  useEffect(() => { setMobileGraphOpen(false); }, [activeConversationId]);
  // Escape closes the graph overlay (matches the SearchModal convention).
  useEffect(() => {
    if (!mobileGraphOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileGraphOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileGraphOpen]);
  const [graphLayers, setGraphLayers] = useState<Map<string, ElementDefinition[]>>(new Map());
  const [activeGraphScope, setActiveGraphScope] = useState<string>('all');
  const [showSearch, setShowSearch] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [showParticipants, setShowParticipants] = useState(false);
  const [externalFocusNodeId, setExternalFocusNodeId] = useState<string | null>(null);
  const [criticalPaths, setCriticalPaths] = useState<Map<string, string[]>>(new Map());
  const titleInputRef = useRef<HTMLInputElement>(null);
  const graphPanelRef = useRef<GraphPanelHandle>(null);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const [graphHoveredLabel, setGraphHoveredLabel] = useState<string | null>(null);
  const [graphOrientation, setGraphOrientation] = useState<'vertical' | 'horizontal'>(() =>
    (localStorage.getItem('cade_graph_layout') as 'vertical' | 'horizontal') ?? 'vertical'
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const lastStreamEventChatRef = useRef<number>(0);

  // Sync activeView from URL query params (handles navigation from other routes like /projects)
  // Also handles ?conversationId= from the browser extension's "Open in full view" (Req 258)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view');
    if (view === 'agents') {
      setActiveView({ type: 'agents' });
      setActiveConversationId(null);
    } else if (view === 'agent-detail') {
      const agentId = params.get('agentId');
      if (agentId) {
        setActiveView({ type: 'agent-detail', agentId });
        setActiveConversationId(null);
      }
    }
    const convId = params.get('conversationId');
    if (convId) {
      setActiveConversationId(convId);
      setActiveView({ type: 'chat' });
    }
  }, [location.search]);
  const createConv = trpc.conversations.create.useMutation();
  const sendMessage = trpc.conversations.sendMessage.useMutation();
  const updateTitle = trpc.conversations.updateTitle.useMutation();
  const utils = trpc.useUtils();
  const activeConv = trpc.conversations.get.useQuery(
    { conversationId: activeConversationId! },
    { enabled: !!activeConversationId },
  );

  // Ontology v2 read-flip (Phase 4). The per-environment CHAT_GRAPH_V2_READS flag
  // (surfaced by the gateway) decides the chat graph data source: when ON we read
  // the persistent graph via conversationGraph.getV2; when OFF (or while the
  // config query is still loading) we fall back to the v1 conversation.graphState
  // path. Spec: core/part4 §2.2.16.
  // Finite staleTime (not Infinity) so a runtime CHAT_GRAPH_V2_READS flip
  // eventually reaches long-lived SPA sessions on remount/refocus.
  const chatGraphConfig = trpc.graph.chatGraphConfig.useQuery(undefined, { staleTime: 5 * 60 * 1000 });
  const v2ReadsEnabled = chatGraphConfig.data?.v2ReadsEnabled ?? false;
  const v2Graph = trpc.graph.conversationGraph.getV2.useQuery(
    { conversationId: activeConversationId! },
    { enabled: v2ReadsEnabled && !!activeConversationId },
  );

  // Listen for graph_data events via WebSocket
  useEffect(() => {
    if (!activeConversationId) return;

    // Phase-4 read-flip: pending getV2 refetch timers, cleared on cleanup so a
    // conversation switch / unmount never fires a stale refetch.
    const refetchTimers: ReturnType<typeof setTimeout>[] = [];

    const unsub = wsManager.subscribe(activeConversationId, (event) => {
      if (event.type === 'graph_data' && 'nodes' in event) {
        // Phase-4 read-flip: the v2 panel reads the PERSISTENT graph, but the v2
        // dual-write lands asynchronously AFTER this graph_data broadcast. An
        // immediate-only refetch would read the pre-turn (stale/empty) rows, so
        // invalidate now (covers the already-persisted/fast path) and retry on a
        // short schedule to catch the async persist landing.
        if (v2ReadsEnabled) {
          const refetch = () => {
            void utils.graph.conversationGraph.getV2.invalidate({ conversationId: activeConversationId });
          };
          refetch();
          refetchTimers.push(setTimeout(refetch, 1500), setTimeout(refetch, 4000));
          return;
        }
        const graphEvent = event as StreamEvent & {
          messageId: string;
          nodes: GraphNode[];
          edges: GraphEdge[];
          criticalPath?: string[];
        };
        const newElements = graphEventToElements(graphEvent) as ElementDefinition[];
        const messageId = graphEvent.messageId;

        setGraphLayers((prev) => {
          const updated = new Map(prev);
          const existing = updated.get(messageId) ?? [];
          const existingMap = new Map(existing.map((el) => [el.data.id as string, el]));
          for (const el of newElements) {
            existingMap.set(el.data.id as string, el);
          }
          updated.set(messageId, Array.from(existingMap.values()));
          return updated;
        });

        // WI-9D: Capture critical path from graph event
        // Resolve node IDs from this graph block to labels, so the renderer
        // can match against actual Cytoscape nodes (which may have different IDs
        // due to dedup merging from earlier graph blocks)
        const criticalPath = graphEvent.criticalPath;
        if (criticalPath && criticalPath.length > 0) {
          const idToLabel = new Map<string, string>();
          for (const n of graphEvent.nodes) {
            if (n.id && n.label) idToLabel.set(n.id, n.label);
          }
          // Store both the original ID and the resolved label for each entry
          const resolved = criticalPath.map((id) => idToLabel.get(id) ?? id);
          setCriticalPaths((prev) => {
            const updated = new Map(prev);
            updated.set(messageId, resolved);
            return updated;
          });
        }
      }
    });

    return () => {
      for (const t of refetchTimers) clearTimeout(t);
      unsub();
    };
  }, [activeConversationId, v2ReadsEnabled, utils]);

  // Track streaming state for stop button
  useEffect(() => {
    if (!activeConversationId) {
      setIsStreaming(false);
      return;
    }

    const unsub = wsManager.subscribe(activeConversationId, (event) => {
      if (event.type === 'text_delta' || event.type === 'reasoning_delta' || event.type === 'tool_call_start') {
        lastStreamEventChatRef.current = Date.now();
        setIsStreaming(true);
      } else if (event.type === 'stream_end' || event.type === 'stream_error') {
        setIsStreaming(false);
      }
    });

    return unsub;
  }, [activeConversationId]);

  // Watchdog: if no streaming events for 15s while streaming, poll DB to check if stream completed
  useEffect(() => {
    if (!isStreaming || !activeConversationId) return;
    const interval = setInterval(async () => {
      if (Date.now() - lastStreamEventChatRef.current > 15_000) {
        const result = await activeConv.refetch();
        const msgs = result.data?.messages as Array<{ status?: string }> | undefined;
        const hasStreamingMsg = msgs?.some((m) => m.status === 'streaming');
        if (!hasStreamingMsg) setIsStreaming(false);
      }
    }, 8_000);
    return () => clearInterval(interval);
  }, [isStreaming, activeConversationId, activeConv]);

  // On WebSocket reconnect, check if active stream completed while disconnected
  useEffect(() => {
    return wsManager.onReconnect(() => {
      if (isStreaming && activeConversationId) {
        void activeConv.refetch().then((result) => {
          const msgs = result.data?.messages as Array<{ status?: string }> | undefined;
          const hasStreamingMsg = msgs?.some((m) => m.status === 'streaming');
          if (!hasStreamingMsg) setIsStreaming(false);
        });
      }
    });
  }, [isStreaming, activeConversationId, activeConv]);

  // Listen for global events — refresh sidebar for new participants and unread messages
  useEffect(() => {
    const unsub = wsManager.subscribe('*', (event: WebSocketEvent) => {
      if (event.type === 'participant_joined' && 'participant' in event) {
        const joined = event as WebSocketEvent & { participant: { userId: string } };
        if (joined.participant.userId === userId) {
          void utils.conversations.list.invalidate();
        }
      }
      // Refresh sidebar badge when a message arrives in a non-active conversation
      if ((event.type === 'stream_end' || event.type === 'new_user_message') && 'conversationId' in event) {
        const evt = event as WebSocketEvent & { conversationId: string };
        if (evt.conversationId !== activeConversationId) {
          void utils.conversations.list.invalidate();
        }
      }
      // Show toast when someone mentions you in a note
      if (event.type === 'mention_notification' && 'mentionedByName' in event) {
        const mention = event as WebSocketEvent & { mentionedByName: string; snippet: string; conversationId: string };
        showToast(`${mention.mentionedByName} mentioned you: "${mention.snippet.slice(0, 60)}${mention.snippet.length > 60 ? '…' : ''}"`, 'info');
        void utils.conversations.list.invalidate();
      }
    });
    return unsub;
  }, [userId, utils, activeConversationId, showToast]);

  // Load persisted graph state when switching conversations
  useEffect(() => {
    if (!activeConversationId) {
      setGraphLayers(new Map());
      setActiveGraphScope('all');
      setCriticalPaths(new Map());
      return;
    }

    // Phase-4 read-flip: when the flag is ON, the persistent-graph v2 projection
    // is the data source. criticalPath is a v1-only concept (attack-chain framing
    // stays in investigations), so it is cleared in v2 mode.
    if (v2ReadsEnabled) {
      const v2 = v2Graph.data as V2GraphResponse | undefined;
      if (v2 && v2.nodes.length > 0) {
        setGraphLayers(new Map([['v2', v2GraphToElements(v2) as ElementDefinition[]]]));
      } else {
        // Empty (no persisted v2 nodes yet, or query in flight): render the
        // empty-state UI rather than blanking the panel.
        setGraphLayers(new Map());
      }
      setActiveGraphScope('all');
      setCriticalPaths(new Map());
      return;
    }

    // Load graph state from conversation data (v1 path)
    const convData = activeConv.data as { graphState?: { nodes?: Array<Record<string, unknown>>; edges?: Array<Record<string, unknown>>; criticalPath?: string[]; lastGraphMessageId?: string } } | undefined;
    if (convData?.graphState?.nodes?.length) {
      const graphKey = convData.graphState.lastGraphMessageId ?? 'persisted';
      const restored = graphEventToElements({
        nodes: convData.graphState.nodes as unknown as GraphNode[],
        edges: (convData.graphState.edges ?? []) as unknown as GraphEdge[],
      }) as ElementDefinition[];
      setGraphLayers(new Map([[graphKey, restored]]));
      setActiveGraphScope('all');

      // Restore persisted critical path (already resolved to labels by the backend)
      if (convData.graphState.criticalPath?.length) {
        setCriticalPaths(new Map([[graphKey, convData.graphState.criticalPath]]));
      }
    } else {
      setGraphLayers(new Map());
      setActiveGraphScope('all');
    }
  }, [activeConversationId, activeConv.data, v2ReadsEnabled, v2Graph.data]);

  // Focus title input when editing starts
  useEffect(() => {
    if (editingTitle) {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }
  }, [editingTitle]);

  async function handleNewConversation() {
    const conv = await createConv.mutateAsync();
    await utils.conversations.list.invalidate();
    withViewTransition(() => {
      setActiveConversationId(conv.id);
      setActiveView({ type: 'chat' });
      navigate('/', { replace: true });
    });
  }

  async function handleNewMessage(text: string, fileIds?: string[]) {
    const conv = await createConv.mutateAsync();
    await utils.conversations.list.invalidate();
    withViewTransition(() => {
      setActiveConversationId(conv.id);
      setActiveView({ type: 'chat' });
      navigate('/', { replace: true });
    });
    await sendMessage.mutateAsync({
      conversationId: conv.id,
      content: text,
      surface: 'web',
      ...(fileIds?.length ? { fileIds } : {}),
    });
    await utils.conversations.get.invalidate({ conversationId: conv.id });
  }

  async function handleTitleSave() {
    if (!activeConversationId || !titleDraft.trim()) {
      setEditingTitle(false);
      return;
    }
    await updateTitle.mutateAsync({ conversationId: activeConversationId, title: titleDraft.trim() });
    await utils.conversations.get.invalidate({ conversationId: activeConversationId });
    await utils.conversations.list.invalidate();
    setEditingTitle(false);
  }

  function handleTitleClick() {
    if (!activeConversationId) return;
    setTitleDraft((activeConv.data?.title as string) ?? 'New chat');
    setEditingTitle(true);
  }

  // Keyboard shortcuts
  const shortcuts = useMemo(() => ({
    'mod+k': () => { setShowSearch((prev) => !prev); },
    'mod+n': () => { void handleNewConversation(); },
    'escape': () => {
      if (showSearch) { setShowSearch(false); return; }
      setActiveConversationId(null);
    },
  }), [showSearch]);

  useKeyboardShortcuts(shortcuts);

  // Memoized combined + deduped view (absorb alerts first, then deduplicate)
  const allGraphElements = useMemo(() => {
    const merged = new Map<string, ElementDefinition>();
    for (const layer of graphLayers.values()) {
      for (const el of layer) {
        merged.set(el.data.id as string, el);
      }
    }
    const afterAbsorption = absorbAlerts(Array.from(merged.values()));
    const withGeo = injectGeolocationNodes(afterAbsorption);
    const normalized = normalizeEdgeDirections(withGeo);
    return deduplicateGraphElements(normalized);
  }, [graphLayers]);

  // Scoped elements based on active scope
  const scopedGraphElements = useMemo(() => {
    if (activeGraphScope === 'all') return allGraphElements;
    const raw = graphLayers.get(activeGraphScope) ?? [];
    return deduplicateGraphElements(normalizeEdgeDirections(injectGeolocationNodes(absorbAlerts(raw))));
  }, [activeGraphScope, allGraphElements, graphLayers]);

  // WI-9D: Compute active critical path based on scope
  const activeCriticalPath = useMemo(() => {
    if (activeGraphScope === 'all') {
      const all: string[] = [];
      for (const path of criticalPaths.values()) all.push(...path);
      return all;
    }
    return criticalPaths.get(activeGraphScope) ?? [];
  }, [activeGraphScope, criticalPaths]);

  // Compute graphNodeLabels map for entity cross-referencing (includes aliases)
  const graphNodeLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const el of allGraphElements) {
      if (el.group === 'nodes' || (!el.group && !el.data.source)) {
        const label = el.data.label as string;
        const id = el.data.id as string;
        if (label) map.set(label.toLowerCase(), id);
        // Also index alias IDs from dedup merging
        const aliases = (el.data.aliases as string[]) ?? [];
        for (const alias of aliases) {
          if (alias && !map.has(alias.toLowerCase())) {
            map.set(alias.toLowerCase(), id);
          }
        }
      }
    }
    return map;
  }, [allGraphElements]);

  // Entity map for chat→graph linking (WI-6) — includes aliases
  const graphEntityMap = useMemo(() => {
    const map = new Map<string, { id: string; type: string; label: string; aliases?: string[] }>();
    for (const el of allGraphElements) {
      if (el.group === 'nodes' || (!el.group && !el.data.source)) {
        const label = (el.data.label as string) ?? '';
        const aliases = (el.data.aliases as string[]) ?? [];
        if (label.length >= 3) {
          map.set(label.toLowerCase(), {
            id: el.data.id as string,
            type: (el.data.type as string) ?? 'unknown',
            label,
            aliases: aliases.length > 0 ? aliases : undefined,
          });
        }
      }
    }
    return map;
  }, [allGraphElements]);

  const graphEntitiesValue = useMemo(() => ({
    graphNodeLabels,
    highlightNode: setHighlightedNodeId,
    centerOnNode: (nodeId: string) => {
      graphPanelRef.current?.centerOnNode(nodeId);
    },
    highlightedEntityLabel: graphHoveredLabel,
  }), [graphNodeLabels, graphHoveredLabel]);

  // Ask Cade from graph node popover (WI-3)
  const handleAskCade = useCallback(async (prompt: string) => {
    if (!activeConversationId) return;
    await sendMessage.mutateAsync({ conversationId: activeConversationId, content: prompt, surface: 'web' });
    await utils.conversations.get.invalidate({ conversationId: activeConversationId });
  }, [activeConversationId, sendMessage, utils]);

  // Chat entity → graph focus (WI-6)
  const handleEntityClick = useCallback((nodeId: string) => {
    setExternalFocusNodeId(nodeId);
  }, []);

  // Handle graph→chat entity hover (highlight ALL matching chips via React context)
  const handleGraphEntityHover = useCallback((nodeLabel: string | null) => {
    setGraphHoveredLabel(nodeLabel);
  }, []);

  // Toggle graph orientation (vertical ↔ horizontal)
  const handleOrientationToggle = useCallback(() => {
    setGraphOrientation(prev => {
      const next = prev === 'vertical' ? 'horizontal' : 'vertical';
      localStorage.setItem('cade_graph_layout', next);
      return next;
    });
  }, []);

  // Handle graph→chat entity click (scroll chat to the message that mentions the entity)
  const handleGraphEntityClick = useCallback((nodeLabel: string) => {
    const chips = document.querySelectorAll(`[data-entity-label]`);
    const matching: Element[] = [];
    chips.forEach(chip => {
      if (chip.getAttribute('data-entity-label')?.toLowerCase() === nodeLabel.toLowerCase()) {
        matching.push(chip);
      }
    });
    const lastChip = matching[matching.length - 1];
    if (lastChip) {
      lastChip.scrollIntoView({ behavior: 'smooth', block: 'center' });
      lastChip.classList.add('entity-chip--highlighted');
      setTimeout(() => lastChip.classList.remove('entity-chip--highlighted'), 600);
    }
  }, []);

  // Get participant count
  const participantCount = (activeConv.data as { participants?: unknown[] } | undefined)?.participants?.length;

  // When the graph is in horizontal orientation, it stacks above chat-main within
  // the chat-column. Otherwise it lives as a full-height sibling of chat-column.
  // The graph panel is shown whenever the active conversation has graph data
  // (no manual collapse — the minimize control was removed).
  const graphVisible = !!activeConversationId && allGraphElements.length > 0;
  // Desktop/tablet: the graph lives inline (side-by-side or stacked). On mobile a
  // fixed-width panel would crowd out the chat, so the graph is shown as a
  // collapsed-by-default overlay toggled from the chat pane (part2 req 577).
  const showGraphInline = graphVisible && !isMobile;
  const showGraphOverlay = graphVisible && isMobile;
  const isHorizontalGraph = graphOrientation === 'horizontal' && showGraphInline;

  const mainContent = (
    activeView.type === 'agents' ? (
      <AgentsListInline
        onSelectAgent={(id) => setActiveView({ type: 'agent-detail', agentId: id })}
        onBack={() => setActiveView({ type: 'chat' })}
      />
    ) : activeView.type === 'agent-detail' ? (
      <AgentDetailInline
        agentId={activeView.agentId}
        onBack={() => setActiveView({ type: 'agents' })}
      />
    ) : activeConversationId ? (
      ((activeConv.data as { messages?: unknown[] } | undefined)?.messages?.length ?? 0) === 0 && !isStreaming ? (
        <div className="home-welcome new-chat">
          <MessageList
            conversationId={activeConversationId}
            graphEntityMap={graphEntityMap}
            onEntityClick={handleEntityClick}
          />
          <MessageInput
            conversationId={activeConversationId}
            isStreaming={isStreaming}
            onSend={() => withViewTransition(() => setIsStreaming(true))}
            participantCount={(activeConv.data as { participants?: unknown[] } | undefined)?.participants?.length ?? 1}
            placeholder="Let's investigate. Where do we start?"
            isNewChat
          />
        </div>
      ) : (
        <>
          <MessageList
            conversationId={activeConversationId}
            graphEntityMap={graphEntityMap}
            onEntityClick={handleEntityClick}
          />
          <MessageInput
            conversationId={activeConversationId}
            isStreaming={isStreaming}
            onSend={() => setIsStreaming(true)}
            participantCount={(activeConv.data as { participants?: unknown[] } | undefined)?.participants?.length ?? 1}
            placeholder={undefined}
          />
        </>
      )
    ) : (
      <div className="home-welcome">
        <HomeScreen
          onNavigate={setActiveConversationId}
          onNewConversation={(text, fileIds) => { void handleNewMessage(text, fileIds); }}
        />
        <MessageInput onNewConversation={(text, fileIds) => { void handleNewMessage(text, fileIds); }} placeholder="Let's investigate. Where do we start?" />
      </div>
    )
  );

  const graphPanelEl = graphVisible ? (
    <GraphPanel
      ref={graphPanelRef}
      elements={scopedGraphElements}
      highlightedNodeId={highlightedNodeId}
      onEntityClick={handleGraphEntityClick}
      onAskCade={handleAskCade}
      graphLayers={graphLayers}
      activeScope={activeGraphScope}
      onScopeChange={setActiveGraphScope}
      externalFocusNodeId={externalFocusNodeId}
      onExternalFocusHandled={() => setExternalFocusNodeId(null)}
      criticalPath={activeCriticalPath}
      onNodeHover={handleGraphEntityHover}
      orientation={graphOrientation}
      emptyCaption={v2ReadsEnabled ? "Persistent graph view — entities appear here as they're persisted from this conversation." : undefined}
    />
  ) : null;

  return (
    <div className="chat-layout">
      <GraphEntitiesContext.Provider value={graphEntitiesValue}>
      <div className="chat-body">
        <ConversationSidebar
          activeId={activeConversationId}
          onSelect={(id) => { closeNav(); withViewTransition(() => { setActiveConversationId(id); setActiveView({ type: 'chat' }); navigate('/', { replace: true }); }); }}
          onNew={() => { closeNav(); void handleNewConversation(); }}
          onSearch={() => { closeNav(); setShowSearch(true); }}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
          onNavigateToAgents={() => { closeNav(); navigate('/?view=agents'); }}
          onNavigateToAgent={(id) => { closeNav(); navigate(`/?view=agent-detail&agentId=${id}`); }}
          onNavigateToProjects={() => { closeNav(); animatedNavigate('/projects'); }}
          onNavigateToProject={(id) => { closeNav(); animatedNavigate(`/projects/${id}`); }}
          onNavigateToCryptoInventory={() => { closeNav(); animatedNavigate('/posture/crypto-inventory'); }}
          onNavigateHome={() => { closeNav(); withViewTransition(() => { setActiveConversationId(null); setActiveView({ type: 'chat' }); navigate('/', { replace: true }); }); }}
          isMobile={isMobile}
          mobileOpen={mobileNavOpen}
          onMobileClose={closeNav}
        />
        {isMobile && mobileNavOpen && <SidebarBackdrop onClose={closeNav} />}

        <div className="chat-column">
          {/* Topbar — now starts right of sidebar */}
          <div className="topbar">
            {isMobile && <MobileNavToggle onOpen={() => { setMobileGraphOpen(false); openNav(); }} />}
            <div className="topbar-title-area">
              {editingTitle ? (
                <input
                  ref={titleInputRef}
                  className="topbar-title-input"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={() => { void handleTitleSave(); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { void handleTitleSave(); }
                    if (e.key === 'Escape') setEditingTitle(false);
                  }}
                />
              ) : (
                <span
                  className={`topbar-title ${activeConversationId ? 'topbar-title-editable' : ''}`}
                  onClick={handleTitleClick}
                >
                  {activeConversationId
                    ? (activeConv.data?.title ?? 'New chat')
                    : ''}
                </span>
              )}
              {activeConversationId && (
                <div className="topbar-participants-wrap">
                  <button
                    className="topbar-participant-count"
                    title="Manage participants"
                    onClick={() => setShowParticipants((prev) => !prev)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <line x1="19" y1="8" x2="19" y2="14"/>
                      <line x1="22" y1="11" x2="16" y2="11"/>
                    </svg>
                    {participantCount != null && participantCount > 0 && (
                      <span>{participantCount}</span>
                    )}
                  </button>
                  {showParticipants && (
                    <ParticipantPanel
                      conversationId={activeConversationId}
                      onClose={() => setShowParticipants(false)}
                    />
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <LayoutOrientationToggle
                orientation={graphOrientation}
                onToggle={handleOrientationToggle}
                visible={showGraphInline}
              />
              <DarkModeToggle />
              <UserAvatarDropdown />
            </div>
          </div>

          {/* Chat content — graph and chat-main always live inside chat-content-area so the topbar above
              naturally spans both columns and stays pinned (scrolling happens inside chat-main). */}
          <div className={`chat-content-area ${isHorizontalGraph ? 'layout-horizontal' : 'layout-vertical'} ${isMobile ? 'layout-mobile' : ''}`}>
            <main className="chat-main">{mainContent}</main>
            {showGraphInline && graphPanelEl}

            {/* Mobile: floating toggle (top-right of chat pane) expands the graph overlay */}
            {showGraphOverlay && !mobileGraphOpen && (
              <button
                type="button"
                className="graph-mobile-toggle"
                onClick={() => setMobileGraphOpen(true)}
                title="Show graph"
                aria-label="Show graph"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="5" cy="6" r="2" />
                  <circle cx="19" cy="6" r="2" />
                  <circle cx="12" cy="18" r="2" />
                  <line x1="6.7" y1="7.3" x2="10.6" y2="16.5" />
                  <line x1="17.3" y1="7.3" x2="13.4" y2="16.5" />
                  <line x1="7" y1="6" x2="17" y2="6" />
                </svg>
              </button>
            )}
            {showGraphOverlay && mobileGraphOpen && (
              <div className="graph-mobile-overlay">
                <button
                  type="button"
                  className="graph-mobile-close"
                  onClick={() => setMobileGraphOpen(false)}
                  title="Hide graph"
                  aria-label="Hide graph"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
                {graphPanelEl}
              </div>
            )}
          </div>
        </div>
      </div>
      </GraphEntitiesContext.Provider>

      {/* Search Modal */}
      {showSearch && (
        <SearchModal
          onSelect={(id) => setActiveConversationId(id)}
          onClose={() => setShowSearch(false)}
        />
      )}
    </div>
  );
}
